import { CommonModule } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	inject,
	NgModule,
	ViewChild,
} from '@angular/core';
import { Capacitor } from '@capacitor/core';

import { SessionService } from '~app-client/core/services';
import { CLIENT_ENVIRONMENT } from '~app-client/core/tokens';
import { ClientEnvironmentName } from '~app-client/core/types';
import { ReachStorage } from '~app-client/core/utils';
import { ReachBottomMenuComponent, ReachBottomMenuModule } from '~app-ui/menus/bottom-menu';

import {
	detectBrowser,
	mobilePlatform,
	ReachBrowser,
	reachBrowserImageUrl,
	reachBrowserName,
} from './utils';

@Component({
	standalone: false,

	selector: 'open-mobile-menu',
	templateUrl: './open-mobile-menu.component.html',
	styleUrls: ['./open-mobile-menu.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OpenMobileMenuComponent implements AfterViewInit {
	private readonly elementRef = inject(ElementRef<HTMLElement>);
	private readonly environment = inject(CLIENT_ENVIRONMENT);
	private readonly sessionService = inject(SessionService);

	public static readonly storageKey = '__openMobileMenuOpened';
	public static readonly msToShowAgain = 1000 * 60 * 60 * 6; // 6 hours

	public static get storedValue(): number {
		const storedStr = ReachStorage.getItem(OpenMobileMenuComponent.storageKey) || '';
		const stored = Number(storedStr);

		return Number.isInteger(stored) ? stored : 0;
	}

	public static get showMenuByTime(): boolean {
		const msDiff = Date.now() - OpenMobileMenuComponent.storedValue;
		return msDiff > OpenMobileMenuComponent.msToShowAgain;
	}

	public static showMenuByRegistrationTime(date: number | Date): boolean {
		const cleanDate = new Date(date || Date.now()).valueOf();
		return Date.now() - cleanDate > 6 * 3600 * 1000;
	}

	public readonly reachKey = ReachBrowser.REACH;
	public readonly browserKey = detectBrowser();

	private readonly mobilePlatform = mobilePlatform();
	private readonly validPlatform = this.mobilePlatform !== 'unknown';
	private readonly isNative = Capacitor.isNativePlatform();
	private readonly isDebug = this.environment?.environmentName === ClientEnvironmentName.LOCAL;
	private readonly validMobile =
		(this.isDebug && !this.isNative) || (!this.isNative && this.validPlatform);
	public readonly showMenu =
		this.validMobile &&
		OpenMobileMenuComponent.showMenuByTime &&
		OpenMobileMenuComponent.showMenuByRegistrationTime(
			this.sessionService.user?.activatedAt ?? Date.now()
		);

	@ViewChild(ReachBottomMenuComponent)
	private menu?: ReachBottomMenuComponent;

	private reachClicked = false;

	private readonly iosUrl = 'https://apps.apple.com/es/app/reach-mobile-app/id1570293911';
	private readonly androidUrl = 'https://play.google.com/store/apps/details?id=ch.rea.app';

	ngAfterViewInit(): void {
		if (this.showMenu) {
			this.menu?.open();
		} else {
			this.elementRef?.nativeElement.remove();
		}
	}

	public itemImageUrl(item: ReachBrowser): string {
		return reachBrowserImageUrl[item];
	}

	public itemName(item: ReachBrowser): string {
		return reachBrowserName[item];
	}

	public isReach(item: ReachBrowser): boolean {
		return item === ReachBrowser.REACH;
	}

	public openKey(item: ReachBrowser): void {
		if (item === ReachBrowser.REACH) {
			if (!this.reachClicked) {
				this.reachClicked = true;
			}
			this.openMobileApp();
		} else {
			this.menu?.close();
		}
	}

	public onMenuOpened(): void {}

	public onMenuClosed(): void {
		ReachStorage.setItem(OpenMobileMenuComponent.storageKey, `${Date.now()}`);
	}

	private openMobileApp(): void {
		if (this.mobilePlatform === 'android') {
			window.open(this.androidUrl, '_blank')?.focus();
		} else {
			window.open(this.iosUrl, '_blank')?.focus();
		}
	}
}

@NgModule({
	declarations: [OpenMobileMenuComponent],
	imports: [CommonModule, ReachBottomMenuModule],
	exports: [OpenMobileMenuComponent],
})
export class OpenMobileMenuModule {}
